import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { getAcompanhamento } from '@/lib/api';

// [CNJ-WATCH 21/09] Acompanhamento de processo (DataJud): casos vigiados a pedido do
// usuário (botão "Acompanhar processo" no card) ou por auto-enroll default-ON, e os
// reportes enviados (digest de e-mail + sino). Fonte: routers/acompanhamento.py.
// Sem filtro de data global — o estado da fila é "agora"; séries fixas em 30 dias.

interface Resumo {
  total: number; ativos: number; desligados: number; orgs_ativas: number;
  ativos_usuario: number; ativos_auto: number; ativos_seed: number;
  checados_24h: number; checados_7d: number; nunca_checados: number;
  com_movimento_novo: number; notificados: number;
  ultima_consulta: string | null; primeiro_real: string | null; ultimo_inscrito: string | null;
  fila_cap_dia: number; fila_dias_para_cobrir: number;
  digests: number; digests_entregues: number; digests_abertos: number; digests_clicados: number; digests_bounce: number;
  alertas_enviados: number; sino_total: number; sino_lidos: number;
}
interface Caso {
  id: number; processo_id: number; org_id: number; org_nome: string | null; cnj: string; ativo: boolean;
  origem: 'usuario' | 'auto' | 'seed'; created_at: string; last_checked_at: string | null;
  ultimo_movimento_data: string | null; ultimo_movimento_nome: string | null; movimento_notificado_data: string | null;
  filename: string | null; processo_status: string | null; processo_created_at: string | null;
  user_email: string | null; user_nome: string | null; n_reportes: number;
}
interface Alerta { processo_id: number; filename: string | null; cnj: string | null; movimento: string | null; sino_lido_em: string | null; ainda_acompanhado: boolean | null }
interface Digest {
  email_id: string; enviado_em: string; recipient: string; user_nome: string | null; org_id: number | null; org_nome: string | null;
  alertas: Alerta[]; eventos: Record<string, string>;
}
interface Sino { id: number; org_id: number; org_nome: string | null; title: string; body: string | null; link: string | null; created_at: string; read_at: string | null }
interface Dia { d: string; inscritos: number; inscritos_usuario: number; consultados: number; digests: number; alertas: number }
// [RETORNO 11/10] impacto do reporte: quem voltou ao produto e quem comprou depois.
interface ImpUser {
  email: string; user_id: number | null; org_id: number | null; org_nome: string | null; nome: string | null;
  primeiro_reporte_em: string; ultimo_reporte_em: string | null;
  n_reportes: number; n_digests: number; n_casos_reportados: number;
  abriu_em: string | null; clicou_em: string | null; bounce: boolean; last_login: string | null;
  login_em: string | null; pageview_em: string | null; caso_novo_em: string | null; n_casos_novos_apos: number;
  comprou_em: string | null; n_compras_apos: number; creditos_apos: number; valor_apos: number;
  pacotes_apos: string | null; comprava_antes: boolean;
  voltou: boolean; voltou_em: string | null; sinal: 'login' | 'caso_novo' | 'pageview' | null;
  horas_para_voltar: number | null; comprou: boolean; horas_para_comprar: number | null; maduro_48h: boolean;
}
interface ImpResumo {
  impactados: number; voltaram: number; voltaram_pct: number | null; compraram: number; compraram_pct: number | null;
  compraram_novos: number; ja_compravam: number; valor_total_apos: number; creditos_total_apos: number;
  casos_novos_apos: number; abriram: number; clicaram: number;
  med_h_reporte_retorno: number | null; med_h_reporte_compra: number | null;
  sinal_login: number; sinal_caso_novo: number; sinal_pageview: number;
  maduros_48h: number; maduros_voltaram: number; maduros_compraram: number;
  baseline: { orgs: number; janela_dias: number; voltaram: number; voltaram_pct: number | null; compraram: number; compraram_pct: number | null };
}
interface Impacto { resumo: ImpResumo; usuarios: ImpUser[]; nota: string }
interface Data {
  disponivel: boolean; erro?: string;
  flags: Record<string, string | number>;
  resumo: Resumo; serie_30d: Dia[]; top_movimentos: { nome: string; n: number }[];
  casos: Caso[]; digests: Digest[]; sino: Sino[]; nota_origem: string | null;
  impacto?: Impacto;
}

const dt = (v: string | null | undefined, comHora = true) => {
  if (!v) return '—';
  const d = new Date(v.endsWith('Z') || v.includes('+') ? v : v + 'Z');
  return comHora ? d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
};
const cnjFmt = (c: string | null | undefined) =>
  c && /^\d{20}$/.test(c) ? `${c.slice(0, 7)}-${c.slice(7, 9)}.${c.slice(9, 13)}.${c[13]}.${c.slice(14, 16)}.${c.slice(16)}` : (c || '—');
const label = (c: { filename: string | null; processo_id: number; cnj?: string | null }) => {
  const m = (c.filename || '').match(/\d{7}-\d{2}\.\d{4}\.\d\.\d{2}\.\d{4}/);
  return m ? m[0] : (c.cnj && /^\d{20}$/.test(c.cnj) ? cnjFmt(c.cnj) : `caso #${c.processo_id}`);
};
const pct = (v: number | null | undefined) => (v === null || v === undefined ? '—' : `${(v * 100).toFixed(1)}%`);
const brl = (v: number | null | undefined) => (v ? `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—');
const horas = (h: number | null | undefined) => {
  if (h === null || h === undefined) return '—';
  if (h < 1) return `${Math.round(h * 60)} min`;
  return h < 48 ? `${h.toFixed(1)} h` : `${(h / 24).toFixed(1)} d`;
};
const SINAL_TXT: Record<string, { t: string; cls: string }> = {
  login: { t: 'login', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  caso_novo: { t: 'caso novo', cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  pageview: { t: 'abriu o app', cls: 'bg-sky-50 text-sky-700 border-sky-200' },
};
const ORIGEM_TXT: Record<string, { t: string; cls: string }> = {
  usuario: { t: 'pedido do usuário', cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  auto: { t: 'automático', cls: 'bg-slate-50 text-slate-600 border-slate-200' },
  seed: { t: 'teste interno', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
};

function Kpi({ label, value, sub, tone }: { label: string; value: string | number; sub?: string; tone?: 'ok' | 'warn' | 'bad' }) {
  const cls = tone === 'ok' ? 'border-emerald-200 bg-emerald-50' : tone === 'warn' ? 'border-amber-200 bg-amber-50' : tone === 'bad' ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-white';
  return (
    <div className={`rounded-lg border px-4 py-3 ${cls}`}>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-2xl font-bold text-slate-800 tabular-nums">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function Barras({ serie, campo, cor, titulo }: { serie: Dia[]; campo: keyof Dia; cor: string; titulo: string }) {
  const max = Math.max(1, ...serie.map((s) => Number(s[campo]) || 0));
  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="text-xs font-semibold text-slate-600 mb-2">{titulo} <span className="text-slate-400 font-normal">· 30 dias · máx {max}</span></div>
      <div className="flex items-end gap-[2px] h-16">
        {serie.map((s) => {
          const v = Number(s[campo]) || 0;
          return (
            <div key={s.d} title={`${dt(s.d, false)}: ${v}`} className="flex-1 flex flex-col justify-end h-full">
              <div className={`${cor} rounded-sm`} style={{ height: `${Math.max(v > 0 ? 6 : 1, (v / max) * 100)}%`, opacity: v > 0 ? 1 : 0.25 }} />
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-[10px] text-slate-400 mt-1"><span>{dt(serie[0]?.d, false)}</span><span>{dt(serie[serie.length - 1]?.d, false)}</span></div>
    </div>
  );
}

export function AcompanhamentoPage() {
  const [d, setD] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [aba, setAba] = useState<'casos' | 'reportes' | 'retorno' | 'sino'>('casos');
  const [ordemImp, setOrdemImp] = useState<'reporte' | 'retorno' | 'compra'>('reporte');
  const [filtroImp, setFiltroImp] = useState<'todos' | 'voltou' | 'comprou'>('todos');
  const [incluirInternos, setIncluirInternos] = useState(false);
  const [ativo, setAtivo] = useState<'todos' | 'ativos' | 'desligados'>('ativos');
  const [origem, setOrigem] = useState<'todas' | 'usuario' | 'auto' | 'seed'>('todas');
  const [q, setQ] = useState('');
  const [qAplicado, setQAplicado] = useState('');
  const [digestAberto, setDigestAberto] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    getAcompanhamento({ incluir_internos: incluirInternos, ativo, origem, q: qAplicado || undefined, limit: 400 })
      .then((r) => setD(r.data)).catch(() => setD(null)).finally(() => setLoading(false));
  }, [incluirInternos, ativo, origem, qAplicado]);

  const r = d?.resumo;
  const pendentesFila = useMemo(() => (r ? Math.max(0, r.ativos - r.checados_24h) : 0), [r]);
  const impUsuarios = useMemo(() => {
    const lista = d?.impacto?.usuarios ?? [];
    const f = lista.filter((u) => (filtroImp === 'voltou' ? u.voltou : filtroImp === 'comprou' ? u.comprou : true));
    const key = (u: ImpUser) => (ordemImp === 'compra' ? u.comprou_em : ordemImp === 'retorno' ? u.voltou_em : u.primeiro_reporte_em) || '';
    // desc, sem data vai pro fim ('' é o menor)
    return [...f].sort((a, b) => (key(a) < key(b) ? 1 : key(a) > key(b) ? -1 : 0));
  }, [d, ordemImp, filtroImp]);

  if (!d && loading) return <div className="p-6 text-sm text-slate-500">Carregando acompanhamentos…</div>;
  if (!d || !d.disponivel || !r) return <div className="p-6 text-sm text-red-600">Painel indisponível{d?.erro ? `: ${d.erro}` : ''}.</div>;

  const ligado = String(d.flags.CNJ_WATCH_ENABLED) === '1';
  const dry = String(d.flags.CNJ_WATCH_DRYRUN) === '1';
  const autoOn = String(d.flags.CNJ_WATCH_AUTOENROLL) !== '0';
  const taxa = (n: number) => (r.digests ? `${Math.round((n / r.digests) * 100)}%` : '—');

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-xl font-bold text-slate-800">Acompanhamento de processo</h1>
        <p className="text-sm text-slate-500">
          Casos vigiados no DataJud/CNJ (botão “Acompanhar processo” no card do caso + auto-enroll default-ON) e os reportes
          enviados aos usuários quando há movimento relevante (digest por e-mail, 1 por usuário/dia + sino).
        </p>
      </div>

      {/* Estado do serviço */}
      <div className={`rounded-lg border px-4 py-3 text-sm ${!ligado ? 'border-red-300 bg-red-50 text-red-800' : dry ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-emerald-200 bg-emerald-50 text-emerald-800'}`}>
        <span className="font-semibold">Serviço:</span>{' '}
        {!ligado ? 'DESLIGADO (CNJ_WATCH_ENABLED ≠ 1) — nada é consultado nem enviado.' : dry ? 'DRY-RUN — consulta e lista, mas não envia.' : 'ligado — consulta e envia.'}
        {' '}· auto-enroll {autoOn ? 'ON' : 'OFF'} (janela {d.flags.CNJ_WATCH_ENROLL_DIAS}d)
        {' '}· cap {d.flags.CNJ_WATCH_MAX_PER_RUN} consultas e {d.flags.CNJ_WATCH_MAX_EMAILS} destinatários por ciclo
        {' '}· {d.flags.cron} · última consulta {dt(r.ultima_consulta)}
      </div>

      {/* KPIs */}
      <div className="grid gap-3 grid-cols-2 md:grid-cols-4 xl:grid-cols-6">
        <Kpi label="Acompanhados (ativos)" value={r.ativos} sub={`${r.orgs_ativas} orgs · ${r.desligados} desligaram`} />
        <Kpi label="A pedido do usuário" value={r.ativos_usuario} sub={`${r.ativos_auto} automáticos${r.ativos_seed ? ` · ${r.ativos_seed} teste` : ''}`} tone={r.ativos_usuario > 0 ? 'ok' : undefined} />
        <Kpi label="Cobertura da fila" value={`${r.fila_dias_para_cobrir}d`} sub={`${r.fila_cap_dia}/dia ⇒ cada caso checado a cada ~${r.fila_dias_para_cobrir} dias`} tone={r.fila_dias_para_cobrir > 14 ? 'bad' : r.fila_dias_para_cobrir > 7 ? 'warn' : undefined} />
        <Kpi label="Checados 24h / 7d" value={`${r.checados_24h} / ${r.checados_7d}`} sub={`${pendentesFila} aguardam · ${r.nunca_checados} nunca checados`} tone={r.nunca_checados > 0 ? 'warn' : undefined} />
        <Kpi label="Movimento após inscrição" value={r.com_movimento_novo} sub={`${r.notificados} com aviso já enviado`} />
        <Kpi label="Reportes (digests)" value={r.digests} sub={`${r.alertas_enviados} alertas · entregue ${taxa(r.digests_entregues)} · aberto ${taxa(r.digests_abertos)} · clique ${taxa(r.digests_clicados)}${r.digests_bounce ? ` · ${r.digests_bounce} bounce` : ''}`} tone={r.digests_bounce ? 'bad' : undefined} />
      </div>

      {/* Séries 30d */}
      <div className="grid gap-3 md:grid-cols-3">
        <Barras serie={d.serie_30d} campo="inscritos" cor="bg-slate-400" titulo="Inscritos por dia" />
        <Barras serie={d.serie_30d} campo="consultados" cor="bg-sky-400" titulo="Consultas ao DataJud por dia" />
        <Barras serie={d.serie_30d} campo="alertas" cor="bg-emerald-500" titulo="Alertas enviados por dia" />
      </div>

      {/* Abas + filtros */}
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 pb-2">
        {([['casos', `Casos acompanhados (${d.casos.length})`], ['reportes', `Reportes enviados (${d.digests.length})`],
           ['retorno', `Retorno (${d.impacto?.usuarios.length ?? 0})`], ['sino', `Sino (${d.sino.length})`]] as const).map(([k, t]) => (
          <button key={k} type="button" onClick={() => setAba(k)}
            className={`rounded-md px-3 py-1.5 text-sm font-semibold ${aba === k ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>{t}</button>
        ))}
        <span className="flex-1" />
        {aba === 'casos' && (
          <>
            <select value={ativo} onChange={(e) => setAtivo(e.target.value as never)} className="rounded border border-slate-300 px-2 py-1 text-xs">
              <option value="ativos">ativos</option><option value="desligados">desligados (opt-out)</option><option value="todos">todos</option>
            </select>
            <select value={origem} onChange={(e) => setOrigem(e.target.value as never)} className="rounded border border-slate-300 px-2 py-1 text-xs">
              <option value="todas">qualquer origem</option><option value="usuario">pedido do usuário</option><option value="auto">automático</option><option value="seed">teste interno</option>
            </select>
            <form onSubmit={(e) => { e.preventDefault(); setQAplicado(q.trim()); }} className="flex gap-1">
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="caso #, CNJ, arquivo, org, e-mail" className="rounded border border-slate-300 px-2 py-1 text-xs w-56" />
              <button type="submit" className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50">buscar</button>
            </form>
          </>
        )}
        {aba === 'retorno' && (
          <>
            <select value={filtroImp} onChange={(e) => setFiltroImp(e.target.value as never)} className="rounded border border-slate-300 px-2 py-1 text-xs">
              <option value="todos">todos os impactados</option><option value="voltou">só quem voltou</option><option value="comprou">só quem comprou</option>
            </select>
            <select value={ordemImp} onChange={(e) => setOrdemImp(e.target.value as never)} className="rounded border border-slate-300 px-2 py-1 text-xs">
              <option value="reporte">ordenar por 1º reporte</option><option value="retorno">ordenar por retorno</option><option value="compra">ordenar por compra</option>
            </select>
          </>
        )}
        <label className="flex items-center gap-1 text-xs text-slate-500">
          <input type="checkbox" checked={incluirInternos} onChange={(e) => setIncluirInternos(e.target.checked)} /> incluir orgs internas (2/3/4)
        </label>
        {loading && <span className="text-xs text-slate-400">atualizando…</span>}
      </div>

      {aba === 'casos' && (
        <>
          <div className="rounded-lg border border-slate-200 overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-50 uppercase text-slate-500">
                <tr>{['caso', 'org / usuário', 'origem', 'inscrito', 'último check', 'último movimento (DataJud)', 'aviso enviado', 'reportes', 'estado'].map((h) => <th key={h} className="px-2 py-1.5 text-left whitespace-nowrap">{h}</th>)}</tr>
              </thead>
              <tbody>
                {d.casos.map((c) => {
                  const o = ORIGEM_TXT[c.origem] || ORIGEM_TXT.auto;
                  const movNovo = !!(c.ultimo_movimento_data && c.ultimo_movimento_data > c.created_at);
                  return (
                    <tr key={c.id} className={`border-t border-slate-100 ${!c.ativo ? 'opacity-60' : ''}`}>
                      <td className="px-2 py-1 whitespace-nowrap">
                        <Link to={`/cases/${c.processo_id}` as never} className="text-blue-600 hover:underline font-medium">{label(c)}</Link>
                        <div className="text-[10px] text-slate-400">#{c.processo_id} · {c.processo_status || '—'}</div>
                      </td>
                      <td className="px-2 py-1 max-w-[220px]">
                        <div className="truncate" title={c.org_nome || ''}>{c.org_nome || `org ${c.org_id}`}</div>
                        <div className="text-[10px] text-slate-400 truncate" title={c.user_email || ''}>{c.user_nome || c.user_email || '—'}</div>
                      </td>
                      <td className="px-2 py-1 whitespace-nowrap"><span className={`rounded border px-1.5 py-0.5 text-[10px] font-semibold ${o.cls}`}>{o.t}</span></td>
                      <td className="px-2 py-1 whitespace-nowrap tabular-nums">{dt(c.created_at)}</td>
                      <td className="px-2 py-1 whitespace-nowrap tabular-nums">{dt(c.last_checked_at)}</td>
                      <td className="px-2 py-1 max-w-[260px]">
                        <div className="truncate" title={c.ultimo_movimento_nome || ''}>{c.ultimo_movimento_nome || <span className="text-slate-400">sem movimento no DataJud</span>}</div>
                        <div className={`text-[10px] ${movNovo ? 'text-emerald-700 font-semibold' : 'text-slate-400'}`}>{dt(c.ultimo_movimento_data, false)}{movNovo ? ' · após a inscrição' : ''}</div>
                      </td>
                      <td className="px-2 py-1 whitespace-nowrap tabular-nums">{c.movimento_notificado_data ? <span className="text-emerald-700 font-semibold">{dt(c.movimento_notificado_data, false)}</span> : '—'}</td>
                      <td className="px-2 py-1 text-center tabular-nums">{c.n_reportes || '—'}</td>
                      <td className="px-2 py-1 whitespace-nowrap">{c.ativo ? <span className="text-emerald-700">acompanhando</span> : <span className="text-red-600">desligado</span>}</td>
                    </tr>
                  );
                })}
                {d.casos.length === 0 && <tr><td colSpan={9} className="px-2 py-3 text-slate-400">Nenhum caso com esses filtros.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-slate-200 p-4 text-sm">
              <h2 className="font-semibold text-slate-700 mb-2">Último movimento visto (ativos já consultados)</h2>
              <table className="text-xs"><tbody>
                {d.top_movimentos.map((m) => <tr key={m.nome}><td className="pr-4 py-0.5">{m.nome}</td><td className="py-0.5 tabular-nums">{m.n}</td></tr>)}
              </tbody></table>
              <p className="mt-2 text-[11px] text-slate-400">Só movimentos relevantes (procedência, liquidação, cálculo, execução, homologação, trânsito, embargos…) posteriores à inscrição geram reporte.</p>
            </div>
            <div className="rounded-lg border border-slate-200 p-4 text-xs text-slate-500">
              <h2 className="font-semibold text-slate-700 text-sm mb-2">Como ler a origem</h2>
              <p>{d.nota_origem ?? "'usuario' = clique no botão 'Acompanhar processo'; 'auto' = auto-enroll do poller diário; 'seed' = testes internos (07/09). Gravado na tabela desde 21/09."}</p>
              <p className="mt-2">Lista limitada a {d.casos.length >= 400 ? '400 (use a busca)' : d.casos.length}; casos com aviso enviado vêm primeiro.</p>
            </div>
          </div>
        </>
      )}

      {aba === 'reportes' && (
        <div className="rounded-lg border border-slate-200 overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-50 uppercase text-slate-500">
              <tr>{['enviado', 'destinatário', 'org', 'casos no digest', 'entregue', 'aberto', 'clicou', 'sino lido', ''].map((h, i) => <th key={i} className="px-2 py-1.5 text-left whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody>
              {d.digests.map((g) => {
                const lidos = g.alertas.filter((a) => a.sino_lido_em).length;
                const aberto = digestAberto === g.email_id;
                return (
                  <Fragment key={g.email_id}>
                    <tr className="border-t border-slate-100">
                      <td className="px-2 py-1 whitespace-nowrap tabular-nums">{dt(g.enviado_em)}</td>
                      <td className="px-2 py-1"><div>{g.user_nome || '—'}</div><div className="text-[10px] text-slate-400">{g.recipient}</div></td>
                      <td className="px-2 py-1 max-w-[180px] truncate" title={g.org_nome || ''}>{g.org_nome || (g.org_id ? `org ${g.org_id}` : '—')}</td>
                      <td className="px-2 py-1 whitespace-nowrap">
                        {g.alertas.slice(0, 3).map((a) => <div key={a.processo_id}><Link to={`/cases/${a.processo_id}` as never} className="text-blue-600 hover:underline">{label(a)}</Link></div>)}
                        {g.alertas.length > 3 && <div className="text-slate-400">+{g.alertas.length - 3}</div>}
                      </td>
                      <td className="px-2 py-1 whitespace-nowrap">{g.eventos.delivered ? <span className="text-emerald-700">✓ {dt(g.eventos.delivered)}</span> : g.eventos.bounced || g.eventos.failed ? <span className="text-red-600">✗ bounce</span> : <span className="text-slate-400">—</span>}</td>
                      <td className="px-2 py-1 whitespace-nowrap">{g.eventos.opened ? <span className="text-emerald-700">✓ {dt(g.eventos.opened)}</span> : <span className="text-slate-400">—</span>}</td>
                      <td className="px-2 py-1 whitespace-nowrap">{g.eventos.clicked ? <span className="text-emerald-700">✓ {dt(g.eventos.clicked)}</span> : <span className="text-slate-400">—</span>}</td>
                      <td className="px-2 py-1 whitespace-nowrap tabular-nums">{lidos}/{g.alertas.length}</td>
                      <td className="px-2 py-1"><button type="button" onClick={() => setDigestAberto(aberto ? null : g.email_id)} className="text-blue-600 hover:underline">{aberto ? 'fechar' : 'detalhe'}</button></td>
                    </tr>
                    {aberto && (
                      <tr className="bg-slate-50">
                        <td colSpan={9} className="px-4 py-2">
                          <table className="text-xs w-full"><tbody>
                            {g.alertas.map((a) => (
                              <tr key={a.processo_id} className="border-t border-slate-200/60">
                                <td className="py-1 pr-3 whitespace-nowrap"><Link to={`/cases/${a.processo_id}` as never} className="text-blue-600 hover:underline">{label(a)}</Link> <span className="text-slate-400">#{a.processo_id}</span></td>
                                <td className="py-1 pr-3">{a.movimento || <span className="text-slate-400">(movimento não registrado no sino)</span>}</td>
                                <td className="py-1 pr-3 whitespace-nowrap">{a.sino_lido_em ? `sino lido ${dt(a.sino_lido_em)}` : 'sino não lido'}</td>
                                <td className="py-1 whitespace-nowrap">{a.ainda_acompanhado === false ? <span className="text-red-600">desligou depois</span> : 'segue acompanhando'}</td>
                              </tr>
                            ))}
                          </tbody></table>
                          <div className="text-[10px] text-slate-400 mt-1">email_id {g.email_id}</div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {d.digests.length === 0 && <tr><td colSpan={9} className="px-2 py-3 text-slate-400">Nenhum reporte enviado {incluirInternos ? '' : 'a clientes (os testes internos ficam atrás de “incluir orgs internas”)'}.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {aba === 'retorno' && (!d.impacto ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          A API ainda não devolve o bloco <code>impacto</code> (versão antiga da admin-api no box).
        </div>
      ) : (() => {
        const i = d.impacto.resumo;
        const b = i.baseline;
        return (
          <>
            <div className="grid gap-3 grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
              <Kpi label="Impactados (receberam reporte)" value={i.impactados}
                sub={`${i.abriram} abriram · ${i.clicaram} clicaram · ${i.ja_compravam} já eram pagantes`} />
              <Kpi label="Voltaram depois do reporte" value={pct(i.voltaram_pct)}
                sub={`${i.voltaram} de ${i.impactados} · mediana ${horas(i.med_h_reporte_retorno)} · login ${i.sinal_login} / caso novo ${i.sinal_caso_novo} / app ${i.sinal_pageview}`}
                tone={i.voltaram > 0 ? 'ok' : undefined} />
              <Kpi label="Compraram depois do reporte" value={pct(i.compraram_pct)}
                sub={`${i.compraram} de ${i.impactados} · mediana ${horas(i.med_h_reporte_compra)}`}
                tone={i.compraram > 0 ? 'ok' : undefined} />
              <Kpi label="Compradores NOVOS" value={i.compraram_novos}
                sub={`não compravam antes do 1º reporte (${i.compraram - i.compraram_novos} já compravam)`}
                tone={i.compraram_novos > 0 ? 'ok' : undefined} />
              <Kpi label="R$ após o reporte" value={brl(i.valor_total_apos)}
                sub={`${i.creditos_total_apos} créditos · ${i.casos_novos_apos} casos novos enviados`} />
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div className="rounded-lg border border-slate-200 px-4 py-3 text-xs text-slate-600">
                <div className="font-semibold text-slate-700 text-sm mb-1">Comparação com quem não recebeu reporte</div>
                Orgs com acompanhamento ativo e <span className="font-semibold">sem nenhum reporte</span>: {b.orgs} orgs,
                medidas na mesma duração de janela ({b.janela_dias} dia{b.janela_dias === 1 ? '' : 's'}, mediana do tempo desde o 1º reporte).
                <div className="mt-1 tabular-nums">
                  voltaram <span className="font-semibold">{pct(b.voltaram_pct)}</span> ({b.voltaram}) ·
                  compraram <span className="font-semibold">{pct(b.compraram_pct)}</span> ({b.compraram})
                  <span className="text-slate-400"> vs. impactados {pct(i.voltaram_pct)} / {pct(i.compraram_pct)}</span>
                </div>
                <div className="mt-1 text-[11px] text-slate-400">Baseline é por ORG (o impacto é por destinatário) — serve de ordem de grandeza, não de teste A/B.</div>
              </div>
              <div className="rounded-lg border border-slate-200 px-4 py-3 text-xs text-slate-600">
                <div className="font-semibold text-slate-700 text-sm mb-1">Cohort maduro (≥48 h desde o 1º reporte)</div>
                <span className="tabular-nums">{i.maduros_48h} de {i.impactados} impactados</span> já tiveram tempo de reagir —
                desses, {i.maduros_voltaram} voltaram e {i.maduros_compraram} compraram. Os demais receberam o digest
                nas últimas 48 h: a taxa geral acima ainda está em formação.
                <div className="mt-2 text-[11px] text-slate-400">{d.impacto.nota}</div>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-50 uppercase text-slate-500">
                  <tr>{['usuário', 'org', '1º reporte', 'reportes', 'abriu / clicou', 'voltou', 'comprou', 'já comprava'].map((h) => <th key={h} className="px-2 py-1.5 text-left whitespace-nowrap">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {impUsuarios.map((u) => {
                    const s = u.sinal ? SINAL_TXT[u.sinal] : null;
                    return (
                      <tr key={u.email} className="border-t border-slate-100">
                        <td className="px-2 py-1 max-w-[220px]">
                          <div className="truncate" title={u.nome || ''}>{u.nome || '—'}</div>
                          <div className="text-[10px] text-slate-400 truncate" title={u.email}>{u.email}</div>
                        </td>
                        <td className="px-2 py-1 max-w-[180px] truncate" title={u.org_nome || ''}>{u.org_nome || (u.org_id ? `org ${u.org_id}` : '—')}</td>
                        <td className="px-2 py-1 whitespace-nowrap tabular-nums">
                          {dt(u.primeiro_reporte_em)}
                          {!u.maduro_48h && <div className="text-[10px] text-amber-600">&lt; 48 h</div>}
                        </td>
                        <td className="px-2 py-1 text-center tabular-nums" title={`${u.n_digests} digest(s) · ${u.n_casos_reportados} caso(s)`}>{u.n_reportes}</td>
                        <td className="px-2 py-1 whitespace-nowrap">
                          {u.abriu_em ? <span className="text-emerald-700">✓ abriu</span> : <span className="text-slate-400">não abriu</span>}
                          {u.clicou_em && <span className="text-emerald-700"> · ✓ clicou</span>}
                          {u.bounce && <span className="text-red-600"> · bounce</span>}
                        </td>
                        <td className="px-2 py-1 whitespace-nowrap">
                          {u.voltou ? (
                            <>
                              <span className="text-emerald-700 font-semibold tabular-nums">{dt(u.voltou_em)}</span>
                              <div className="text-[10px] text-slate-500">
                                {s && <span className={`rounded border px-1 py-0.5 font-semibold ${s.cls}`}>{s.t}</span>}
                                <span className="ml-1 tabular-nums">{horas(u.horas_para_voltar)} depois</span>
                                {u.n_casos_novos_apos > 0 && <span className="ml-1">· {u.n_casos_novos_apos} caso(s) novo(s)</span>}
                              </div>
                            </>
                          ) : <span className="text-slate-400">não voltou</span>}
                        </td>
                        <td className="px-2 py-1 whitespace-nowrap">
                          {u.comprou ? (
                            <>
                              <span className="text-emerald-700 font-semibold tabular-nums">{dt(u.comprou_em)}</span>
                              <div className="text-[10px] text-slate-500 tabular-nums">
                                {brl(u.valor_apos)} · {u.n_compras_apos}× {u.pacotes_apos || ''} · {horas(u.horas_para_comprar)} depois
                              </div>
                            </>
                          ) : <span className="text-slate-400">não comprou</span>}
                        </td>
                        <td className="px-2 py-1 whitespace-nowrap">
                          {u.comprava_antes ? <span className="text-slate-600">sim</span> : <span className="text-slate-400">não</span>}
                        </td>
                      </tr>
                    );
                  })}
                  {impUsuarios.length === 0 && <tr><td colSpan={8} className="px-2 py-3 text-slate-400">Nenhum usuário com esses filtros.</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        );
      })())}

      {aba === 'sino' && (
        <div className="rounded-lg border border-slate-200 overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-50 uppercase text-slate-500">
              <tr>{['criada', 'org', 'mensagem', 'caso', 'lida'].map((h) => <th key={h} className="px-2 py-1.5 text-left whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody>
              {d.sino.map((s) => {
                const pid = (s.link || '').match(/caso=(\d+)/)?.[1];
                return (
                  <tr key={s.id} className="border-t border-slate-100">
                    <td className="px-2 py-1 whitespace-nowrap tabular-nums">{dt(s.created_at)}</td>
                    <td className="px-2 py-1 max-w-[180px] truncate" title={s.org_nome || ''}>{s.org_nome || `org ${s.org_id}`}</td>
                    <td className="px-2 py-1">{s.body || s.title}</td>
                    <td className="px-2 py-1 whitespace-nowrap">{pid ? <Link to={`/cases/${pid}` as never} className="text-blue-600 hover:underline">#{pid}</Link> : '—'}</td>
                    <td className="px-2 py-1 whitespace-nowrap">{s.read_at ? <span className="text-emerald-700">✓ {dt(s.read_at)}</span> : <span className="text-slate-400">não</span>}</td>
                  </tr>
                );
              })}
              {d.sino.length === 0 && <tr><td colSpan={5} className="px-2 py-3 text-slate-400">Nenhuma notificação no sino.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
